// ============================================================
// Section « Métiers » de l'espace Statistiques : fonctions pures, sans I/O ni store
// ============================================================
// Les données viennent du satellite du projet (et du groupe, sur NationsGlory) :
//   - Jobs.Players des mods de métiers (JobsMod sur Paladium, RecubeJobsMod sur NationsGlory) : XP cumulée
//     de chaque joueur dans chaque métier, boosts, quêtes du jour ;
//   - le relevé horaire du satellite (1.0.25) : XP gagnée par jour, par heure et par joueur ;
//   - le journal des méthodes des mods (JobsMod 0.0.48, RecubeJobsMod 0.0.12) : XP par action et par cible.
// Les mods ne rangent pas le niveau : le panel lit la courbe dans jobs.config.json de l'image (JobLevels pour
// JobsMod, LevelSettings pour RecubeJobsMod) et la passe au satellite en XP totale par niveau.

/** Ordre fixe des métiers connus : il donne aussi leur couleur (la couleur suit le métier, jamais son rang). */
export const JOB_ORDER = ['miner', 'farmer', 'hunter', 'lumberjack', 'builder', 'engineer'] as const;

const JOB_LABELS: Record<string, string> = {
  miner: 'Mineur',
  farmer: 'Fermier',
  hunter: 'Chasseur',
  lumberjack: 'Bûcheron',
  builder: 'Constructeur',
  engineer: 'Ingénieur',
  fisherman: 'Pêcheur',
  woodcutter: 'Bûcheron',
  enchanter: 'Enchanteur',
};

/** Nom d'un métier en français ; un métier inconnu garde son identifiant, avec une majuscule. */
export function jobLabel(id: string | null | undefined): string {
  if (!id) return '?';
  const known = JOB_LABELS[id.toLowerCase()];
  if (known) return known;
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * Couleur d'un métier : variable CSS `--job-<n>` (huit teintes validées en clair et en sombre, cf. le module de
 * styles de la section). Un métier connu a sa place fixe ; un métier inconnu prend la 7e puis la 8e teinte, au-delà
 * un gris neutre.
 */
export function jobColor(id: string, others: readonly string[] = []): string {
  const fixed = (JOB_ORDER as readonly string[]).indexOf(id);
  if (fixed >= 0) return `var(--job-${fixed + 1})`;
  const extra = others.filter((j) => !(JOB_ORDER as readonly string[]).includes(j)).sort().indexOf(id);
  if (extra === 0) return 'var(--job-7)';
  if (extra === 1) return 'var(--job-8)';
  return 'var(--job-other)';
}

/** Ordre d'affichage des métiers : l'ordre fixe d'abord, puis les inconnus par ordre alphabétique. */
export function sortJobs(ids: readonly string[]): string[] {
  const rank = (id: string) => {
    const i = (JOB_ORDER as readonly string[]).indexOf(id);
    return i >= 0 ? i : JOB_ORDER.length;
  };
  return [...ids].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

// ==================== Courbes de niveaux ====================

/** Courbe d'un métier : XP TOTALE à atteindre pour chaque niveau (1, 2, 3...), strictement croissante. */
export type LevelCurves = Record<string, number[]>;

/** Clé de la courbe commune, prise pour tout métier sans courbe à lui. */
export const COMMON_CURVE = '*';

/**
 * Courbe d'origine de RecubeJobsMod (LevelUtility.LevelsRequiredXp) : XP à gagner pour passer chaque niveau.
 * Elle s'applique quand LevelSettings.TotalXp est absente ou vide.
 */
export const RECUBEJOBS_DEFAULT_STEPS = [
  5124, 8437, 13812, 22541, 37109, 60724, 98483, 158912, 254007, 402156,
  631849, 984321, 1452708, 2054113, 2811042, 3647829, 4521447, 5388912, 6224531, 7000000,
];

/** Niveaux au plus (JobsMod.MaxLevel et RecubeJobsMod.MaxLevel). */
export const MAX_LEVEL = 20;

/** Totaux cumulés à partir des XP par palier ; s'arrête au premier palier nul ou négatif. */
export function cumulate(steps: readonly number[]): number[] {
  const out: number[] = [];
  let total = 0;
  for (const s of steps) {
    if (!Number.isFinite(s) || s <= 0) break;
    total += s;
    out.push(total);
  }
  return out;
}

/** Garde le début strictement croissant d'une liste de totaux, comme le mod (une liste invalide est ignorée). */
function increasing(totals: unknown): number[] | null {
  if (!Array.isArray(totals) || totals.length === 0) return null;
  const out: number[] = [];
  for (const v of totals) {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n) || n <= (out[out.length - 1] ?? 0)) return null;
    out.push(Math.round(n));
  }
  return out.slice(0, MAX_LEVEL);
}

/**
 * JobsMod (Paladium) : section JobLevels = { métier: { "1": xp, "2": xp, ... } }, l'XP à gagner pour passer
 * chaque niveau. Un palier absent vaut 0 pour le mod : la courbe s'arrête avant lui.
 */
export function curvesFromJobLevels(section: unknown): LevelCurves {
  const out: LevelCurves = {};
  if (!section || typeof section !== 'object') return out;
  for (const [job, levels] of Object.entries(section as Record<string, unknown>)) {
    if (!levels || typeof levels !== 'object') continue;
    const steps: number[] = [];
    for (let level = 1; level <= MAX_LEVEL; level++) {
      const v = (levels as Record<string, unknown>)[String(level)];
      steps.push(typeof v === 'number' ? v : Number(v ?? 0));
    }
    const totals = cumulate(steps);
    if (totals.length > 0) out[job.toLowerCase()] = totals;
  }
  return out;
}

/**
 * RecubeJobsMod (NationsGlory) : section LevelSettings = { TotalXp: [...], Jobs: { métier: [...] } }, en XP
 * totale par niveau. TotalXp absente, vide ou invalide : la courbe d'origine du mod. Une courbe de métier invalide
 * est ignorée (le métier suit la courbe commune), comme le fait le mod.
 */
export function curvesFromLevelSettings(section: unknown): LevelCurves {
  const s = (section && typeof section === 'object' ? section : {}) as { TotalXp?: unknown; Jobs?: unknown };
  const out: LevelCurves = { [COMMON_CURVE]: increasing(s.TotalXp) ?? cumulate(RECUBEJOBS_DEFAULT_STEPS) };
  if (s.Jobs && typeof s.Jobs === 'object') {
    for (const [job, totals] of Object.entries(s.Jobs as Record<string, unknown>)) {
      const t = increasing(totals);
      if (t) out[job.toLowerCase()] = t;
    }
  }
  return out;
}

/** Courbe d'un métier : la sienne, sinon la commune, sinon aucune. */
export function curveOf(curves: LevelCurves, job: string): number[] | null {
  return curves[job] ?? curves[COMMON_CURVE] ?? null;
}

/** Niveau d'une XP : nombre de paliers atteints (niveau n dès l'XP totale du niveau n), comme les mods. */
export function levelOf(xp: number, totals: readonly number[]): number {
  let level = 0;
  for (const t of totals) {
    if (xp >= t) level++;
    else break;
  }
  return level;
}

/** Paramètres de la requête des niveaux : un `curve=métier:t1,t2,...` par courbe. */
export function curveParams(curves: LevelCurves): string {
  const p = new URLSearchParams();
  for (const [job, totals] of Object.entries(curves)) {
    if (totals.length > 0) p.append('curve', `${job}:${totals.join(',')}`);
  }
  return p.toString();
}

// ==================== Mise en forme ====================

/** XP compacte : 950, 12,4 k, 3,2 M, 1,1 Md. */
export function formatXp(xp: number): string {
  if (!Number.isFinite(xp)) return '0';
  const a = Math.abs(xp);
  const fmt = (v: number, unit: string) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: v < 10 ? 1 : 0 })} ${unit}`;
  if (a >= 1e9) return fmt(xp / 1e9, 'Md');
  if (a >= 1e6) return fmt(xp / 1e6, 'M');
  if (a >= 1e4) return fmt(xp / 1e3, 'k');
  return Math.round(xp).toLocaleString('fr-FR');
}

/** XP exacte, chiffres groupés (tableaux et infobulles). */
export function formatXpExact(xp: number): string {
  return Math.round(xp).toLocaleString('fr-FR');
}

/** Part en pour cent : une décimale sous 10 %, aucune au-delà. */
export function formatPercent(share: number): string {
  if (!Number.isFinite(share) || share <= 0) return '0 %';
  if (share < 0.1) return '< 0,1 %';
  return `${share.toLocaleString('fr-FR', { maximumFractionDigits: share < 10 ? 1 : 0 })} %`;
}

/** « 26/10 » pour un jour « 2026-10-26 ». */
export function shortDay(date: string): string {
  const [, m, d] = date.split('-');
  return d && m ? `${d}/${m}` : date;
}

/** Jour de la semaine et date : « lun. 26/10 ». */
export function dayLabel(date: string): string {
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return `${d.toLocaleDateString('fr-FR', { weekday: 'short' })} ${shortDay(date)}`;
}

/** Date et heure d'un instant (millisecondes) : « 26/10 à 14:00 ». */
export function formatInstant(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Type d'action d'un gain d'XP, tel que le journal des mods le range. */
const KIND_LABELS: Record<string, string> = {
  break: 'Casser un bloc',
  kill: 'Tuer une créature',
  craft: 'Fabriquer',
  smelt: 'Cuire au four',
  crush: 'Broyer',
  quest: 'Quête du jour',
  command: 'Commande de staff',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** « paladium:amethyst_ore » devient « Amethyst ore (paladium) » ; minecraft: est sous-entendu. */
export function targetLabel(target: string | null | undefined): string {
  if (!target) return '?';
  const [ns, raw] = target.includes(':') ? target.split(':', 2) as [string, string] : ['minecraft', target];
  const name = raw.replace(/_/g, ' ');
  const pretty = name.charAt(0).toUpperCase() + name.slice(1);
  return ns === 'minecraft' ? pretty : `${pretty} (${ns})`;
}

/** Libellé d'une heure de la journée : « 20 h ». */
export function hourLabel(h: number): string {
  return `${h} h`;
}

/**
 * Ce que la page dit d'une réponse en erreur. 404 : le satellite n'a pas encore les routes des métiers (version
 * d'avant la 1.0.25). 403 : le grade n'a pas le droit aux statistiques de jeu. Sinon le message du satellite.
 */
export function jobsError(status: number | undefined, error: string | undefined): string {
  if (status === 404) {
    return 'Le satellite de ce projet ne sert pas encore les statistiques des métiers : il faut le redéployer en version 1.0.25 ou plus.';
  }
  if (status === 403) return "Ton grade n'a pas accès aux statistiques de jeu.";
  return error?.trim() || "Les statistiques des métiers n'ont pas pu être lues.";
}

/** Maximums ronds d'une échelle (× 10^n) et nombre d'intervalles qui tombent chacun sur un nombre rond. */
const NICE_STEPS: [number, number][] = [[1, 4], [2, 4], [2.5, 5], [3, 3], [4, 4], [5, 5], [6, 3], [8, 4], [10, 5]];

/** Valeur ronde au-dessus d'un maximum, pour une échelle lisible (1, 2, 2,5, 3, 4, 5, 6, 8 × 10^n). */
export function niceMax(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  for (const [m] of NICE_STEPS) if (m * pow >= max - 1e-9) return m * pow;
  return 10 * pow;
}

/** Graduations de 0 à `max` (rendu par niceMax), chacune sur un nombre rond : 0, 2, 4, 6 plutôt que 1,5, 4,5. */
export function niceTicks(max: number): number[] {
  const pow = 10 ** Math.floor(Math.log10(max));
  const mantissa = max / pow;
  const parts = NICE_STEPS.find(([m]) => Math.abs(m - mantissa) < 1e-9)?.[1] ?? 4;
  return Array.from({ length: parts + 1 }, (_, i) => (max * i) / parts);
}
